import { Flex, Text } from '@chakra-ui/react';
import { FC } from 'react';

/**
 * Middle-truncated document names for the checklist evidence rows.
 *
 * Attachment names are long, near-identical for their first forty characters
 * ("Exclusive_Right_to_Buy__Buyer_Agency_Contract__SCAR_130_v4_final.pdf"), and
 * differ only in the suffix that says which one this is. A plain
 * `text-overflow: ellipsis` clips exactly the half that carries the answer, so
 * the rows become a column of identical prefixes.
 *
 * CSS has no middle ellipsis, so the name is split into two spans: an elastic
 * head that ellipsises, and a fixed tail that never does. The tail is the
 * trailing name segment plus the extension — the part a reviewer is scanning
 * for.
 */

/**
 * Only `_` and a literal space start a tail. `-` is deliberately excluded: it
 * appears inside single tokens ("1-9", "2026-08-28"), so treating it as a
 * boundary would strand a fragment such as "-9.pdf" as the tail.
 */
const NAME_SEGMENT_SEPARATOR = /[_ ]/;
/** Beyond this the "tail" is no longer a suffix, so fall back to the extension. */
const MAX_TAIL_LENGTH = 20;
/** Below this the head cannot ellipsise usefully, so do not split at all. */
const MIN_HEAD_LENGTH = 4;
const FILE_EXTENSION = /\.[A-Za-z0-9]{1,8}$/;

export interface TruncatedDocumentNameParts {
  /** Ellipsised when the row is too narrow. */
  head: string;
  /** Always rendered in full; empty when the name is too short to split. */
  tail: string;
}

/**
 * Splits a document name into the head that may be clipped and the tail that
 * must survive. Exported for direct testing — the split rule is the whole
 * behaviour, and asserting it through two rendered spans proves less.
 */
export const splitDocumentNameForTruncation = (
  documentName: string
): TruncatedDocumentNameParts => {
  const extensionStart =
    FILE_EXTENSION.exec(documentName)?.index ?? documentName.length;
  const base = documentName.slice(0, extensionStart);

  let tailStart = extensionStart;
  for (let index = base.length - 1; index >= 0; index -= 1) {
    if (NAME_SEGMENT_SEPARATOR.test(base[index])) {
      tailStart = index;
      break;
    }
  }

  // A segment boundary that sits too far back would hand the whole name to the
  // un-ellipsisable tail and overflow the row; keep just the extension instead.
  if (
    documentName.length - tailStart > MAX_TAIL_LENGTH ||
    tailStart < MIN_HEAD_LENGTH
  ) {
    tailStart = extensionStart;
  }

  if (tailStart < MIN_HEAD_LENGTH) {
    return { head: documentName, tail: '' };
  }

  return {
    head: documentName.slice(0, tailStart),
    tail: documentName.slice(tailStart),
  };
};

/**
 * The name renders as two spans, so the full string is only recoverable from
 * the element as a whole — `title` restores it on hover, and callers give the
 * surrounding row an `aria-label` carrying the same name.
 */
export const TruncatedDocumentName: FC<{ documentName: string }> = ({
  documentName,
}) => {
  const { head, tail } = splitDocumentNameForTruncation(documentName);

  return (
    <Flex flex={1} minW={0} title={documentName}>
      <Text
        as="span"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {head}
      </Text>
      {tail ? (
        <Text as="span" whiteSpace="nowrap">
          {tail}
        </Text>
      ) : null}
    </Flex>
  );
};
